import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
  Container,
  MyAccessWrapper,
  Wrapper,
  MyAccessUserName,
  MyAccessUserRoot,
  CustomTabs,
  BackButton,
  Canvas
} from './MyAccess.styles'
import type { TabKey } from './constants'
import { useAccessibleTabs } from './MyAccess.hooks'
import NotificationPreferences from './NotificationPreferences'
import SkipLink, { MAIN_CONTENT_ID } from 'components/skip-link'
import type { User } from 'api/data.types'
import { useTranslation } from 'i18n'
import {
  MY_ACCESS_PATH,
  MY_ACCOUNT_PATH,
  MY_BOOKS_PATH,
  MY_NOTIFICATION_PREFERENCES_PATH
} from 'routes/paths'
import {
  showRenderNotificationPreferences,
  shouldRenderMyAccount
} from 'utils/profile-menu/shouldRenderNotificationPreferences'
import MyConfig from '../my-config'
import AccessCodeViewer from '../access-code-viewer'
import MyBooks from '../my-books'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { useSuperAutorEligible } from 'utils/hooks/use-super-autor-eligible/useSuperAutorEligible'

interface TabItem {
  iconName: string
  label: string
  id: number
  tabKey: TabKey
  route: string
}

function useTabs(user: User, hasCriarModuleAndFlag: boolean): TabItem[] {
  const { t } = useTranslation()

  return useMemo(() => {
    const tabs: Omit<TabItem, 'id'>[] = [
      ...(shouldRenderMyAccount(user)
        ? [
            {
              iconName: 'user',
              label: t('Minha Conta'),
              tabKey: 'MY_ACCOUNT' as TabKey,
              route: MY_ACCOUNT_PATH
            }
          ]
        : []),
      {
        iconName: 'padlock',
        label: t('Meu Acesso'),
        tabKey: 'MY_ACCESS' as TabKey,
        route: MY_ACCESS_PATH
      },
      ...(hasCriarModuleAndFlag
        ? [
            {
              iconName: 'book',
              label: t('Meus Livros'),
              tabKey: 'MY_BOOKS' as TabKey,
              route: MY_BOOKS_PATH
            }
          ]
        : []),
      ...(showRenderNotificationPreferences(user)
        ? [
            {
              iconName: 'bell',
              label: t('Preferências de Notificação'),
              tabKey: 'MY_NOTIFICATION_PREFERENCES' as TabKey,
              route: MY_NOTIFICATION_PREFERENCES_PATH
            }
          ]
        : [])
    ]

    return tabs.map((tab, index) => ({ ...tab, id: index }))
  }, [user, hasCriarModuleAndFlag, t])
}

function getDefaultTabKey(): TabKey {
  if (window.location.pathname.endsWith(MY_NOTIFICATION_PREFERENCES_PATH)) {
    return 'MY_NOTIFICATION_PREFERENCES'
  }
  if (window.location.pathname.endsWith(MY_ACCOUNT_PATH)) {
    return 'MY_ACCOUNT'
  }
  if (window.location.pathname.endsWith(MY_BOOKS_PATH)) {
    return 'MY_BOOKS'
  }
  return 'MY_ACCESS'
}

const MyAccess: React.FC = () => {
  const { user } = useUserInfo()
  const history = useHistory()
  const location = useLocation()
  const { hasCriarModuleAndFlag } = useSuperAutorEligible()
  const [selectedTabKey, setSelectedTabKey] = useState<TabKey>(getDefaultTabKey)
  const tabsContainerRef = useRef<HTMLDivElement>(null)

  const accessTabs = useTabs(user, hasCriarModuleAndFlag)

  const defaultActiveId =
    accessTabs.find(tab => tab.tabKey === selectedTabKey)?.id ?? 0

  useAccessibleTabs({
    containerRef: tabsContainerRef,
    activeIndex: defaultActiveId
  })

  const handleChangeTab = (selected: { id?: string | number }) => {
    const tab = accessTabs.find(t => t.id === selected.id)
    if (tab) {
      setSelectedTabKey(tab.tabKey)
      history.push(tab.route)
    }
  }

  useEffect(() => {
    const body = document.querySelector('body')
    body.style.overflowY = 'scroll'
    return () => {
      body.style.overflowY = 'auto'
    }
  }, [])

  useEffect(() => {
    const tab = accessTabs.find(tab => location.pathname.endsWith(tab.route))
    if (tab) {
      setSelectedTabKey(tab.tabKey)
    }
  }, [accessTabs, location])

  return (
    <Container as="main" id={MAIN_CONTENT_ID} tabIndex={-1}>
      <SkipLink />
      <Canvas id="myCanvas" width="360" height="700" />
      <BackButton
        iconName="arrow-left"
        type="primary"
        onClick={() => history.goBack()}
      />
      <Wrapper>
        <MyAccessWrapper ref={tabsContainerRef}>
          <MyAccessUserName>{user?.name}</MyAccessUserName>
          <MyAccessUserRoot>{user?.reader?.root?.name}</MyAccessUserRoot>
          <CustomTabs
            key={`${location.pathname}-${accessTabs.length}`}
            items={accessTabs}
            onChangeTab={handleChangeTab}
            defaultActive={defaultActiveId}
          />
        </MyAccessWrapper>
        {selectedTabKey === 'MY_ACCOUNT' && <MyConfig user={user} />}
        {selectedTabKey === 'MY_ACCESS' && (
          <AccessCodeViewer history={history} user={user} />
        )}
        {selectedTabKey === 'MY_BOOKS' && <MyBooks />}
        {selectedTabKey === 'MY_NOTIFICATION_PREFERENCES' && (
          <NotificationPreferences user={user} />
        )}
      </Wrapper>
    </Container>
  )
}

export default MyAccess
